import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAction, useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api, type Id } from "../lib/api";
import { useCompany } from "../lib/company";
import { routeIdParam, searchIdParam } from "../domain/routeParams";
import { caughtErrorMessage } from "../domain/errors";
import { formatNullableAuDate } from "../domain/time";
import { Button, Card, Icon, JurisBadge, cx } from "../components/ui";
import { PolicyAlertRecipientsModal } from "../components/PolicyAlertRecipients";
import { businessScopeBadgeToneClass, businessScopeDisplayName } from "../domain/businessScopes";
import { policyStatusLabel, policyStatusToneClass } from "../domain/policies";
import {
  canRunPolicyMapping,
  emptyPolicyMappingWorkflowState,
  emptyPolicySectionDraftState,
  nextPolicyMappingLegislationId,
  nextPolicyMappingWorkflowState,
  nextPolicySectionDraftState,
  nextPolicySectionId,
  policyMappingLegislationIdFromInput,
  policyMappingLegislationOptions,
  policySectionDraftToneClass,
  policySectionDraftView,
  policySectionSaveButtonLabel,
  preferredPolicyMappingLegislationId,
  selectedPolicyMappingLegislation,
  selectedPolicySection,
} from "../domain/policyEditor";
import {
  policyMappingConfidenceLabel,
  policyMappingConfidenceToneClass,
  policyMappingCoverageLabel,
  policyMappingCoverageToneClass,
  type AiPolicyMappingResult,
  type PolicyMappingConfidence,
  type PolicyObligationCoverageStatus,
} from "../domain/policyMapping";
import type { Jurisdiction, PolicyStatus } from "../../convex/model/enums";
import { useUnsavedChanges } from "../lib/useUnsavedChanges";
import { useAccess } from "../lib/access";
import { DocumentDetailsEditor } from "../components/DocumentDetailsEditor";

type PolicyDetail = NonNullable<FunctionReturnType<typeof api.policies.getPolicy>>;

type SubscribedLegislation = {
  _id: Id<"legislation">;
  title: string;
  reference: string;
  jurisdiction: Jurisdiction;
};

type PolicyMappingTarget = {
  title: string;
  subtitle: string;
  jurisdiction: Jurisdiction | null;
};

function policyPortfolioMapUrl(
  policyId: Id<"companyPolicies">,
  sectionId: Id<"companyPolicySections"> | null,
) {
  const params = new URLSearchParams({ policy: policyId });
  if (sectionId !== null) params.set("section", sectionId);
  return `/documents?${params.toString()}`;
}

export function PolicyEditor() {
  const { canWrite } = useAccess();
  const [detailsDirty, setDetailsDirty] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = routeIdParam<"companyPolicies">(params, "id");
  const obligationSetId = searchIdParam<"obligationSets">(searchParams, "obligationSet");
  const requestedSectionId = searchIdParam<"companyPolicySections">(searchParams, "section");
  const alertsRequested = searchParams.get("alerts") === "1";
  const { companyId } = useCompany();
  const policy: PolicyDetail | null | undefined = useQuery(api.policies.getPolicy, companyId !== null && id !== undefined ? { companyId, policyId: id } : "skip");
  const obligationSet = useQuery(
    api.obligationSets.get,
    companyId !== null && obligationSetId !== null ? { companyId, obligationSetId } : "skip",
  );
  const subscriptions = useQuery(api.subscriptions.listForCompany, companyId !== null ? { companyId } : "skip");
  const mapPolicyToLegislation = useAction(api.ai.mapPolicyToLegislation);
  const mapPolicyToObligationSet = useAction(api.ai.mapPolicyToObligationSet);
  const updatePolicySection = useMutation(api.policies.updatePolicySection);

  const [activeSectionId, setActiveSectionId] = useState<Id<"companyPolicySections"> | null>(null);
  const [sectionDraft, setSectionDraft] = useState(() => emptyPolicySectionDraftState());
  const [selectedLegislationId, setSelectedLegislationId] = useState<Id<"legislation"> | null>(null);
  const [mappingWorkflow, setMappingWorkflow] = useState(() => emptyPolicyMappingWorkflowState());
  const [staffAssignmentOpen, setStaffAssignmentOpen] = useState(alertsRequested);
  const mappingState = mappingWorkflow.status;
  const mappingResult = mappingWorkflow.result;
  const mappingError = mappingWorkflow.error;

  const subscribedLegislation = useMemo(
    (): SubscribedLegislation[] => policyMappingLegislationOptions(subscriptions),
    [subscriptions],
  );

  const preferredLegislationId = useMemo(
    () => preferredPolicyMappingLegislationId(policy, subscribedLegislation),
    [policy, subscribedLegislation],
  );

  const sections = useMemo(
    () => policy !== undefined && policy !== null ? policy.sections : [],
    [policy],
  );
  const selectedSection = selectedPolicySection({ sections, activeSectionId });
  const draftView = policySectionDraftView({
    hasCompany: companyId !== null,
    savedBody: selectedSection?.body ?? null,
    draftBody: sectionDraft.body,
    saveState: sectionDraft.saveState,
    saveError: sectionDraft.saveError,
  });
  const draftChanged = draftView.changed;
  useUnsavedChanges(draftChanged || detailsDirty);
  const draftBody = sectionDraft.body;
  const saveState = sectionDraft.saveState;
  const selectedMappingLegislation = selectedPolicyMappingLegislation({
    legislation: subscribedLegislation,
    legislationId: selectedLegislationId,
  });
  const mappingTarget: PolicyMappingTarget | null = obligationSetId !== null
    ? obligationSet === null || obligationSet === undefined
      ? null
      : { title: obligationSet.name, subtitle: `${obligationSet.items.length} saved obligations`, jurisdiction: null }
    : selectedMappingLegislation === null
      ? null
      : {
          title: selectedMappingLegislation.title,
          subtitle: selectedMappingLegislation.reference,
          jurisdiction: selectedMappingLegislation.jurisdiction,
        };
  const mappingResetKey = policy && policy !== null ? policy.updatedAt : null;
  const canSave = canWrite && draftView.canSave;
  const canMap = obligationSetId !== null
    ? companyId !== null &&
      id !== undefined &&
      obligationSet !== null &&
      obligationSet !== undefined &&
      obligationSet.items.length > 0 &&
      sections.length > 0 &&
      !draftChanged &&
      mappingState !== "running"
    : canRunPolicyMapping({
        hasCompany: companyId !== null,
        hasPolicy: id !== undefined,
        selectedLegislationId,
        sectionCount: sections.length,
        draftChanged,
        mappingState,
      });

  useEffect(() => {
    const requested = requestedSectionId !== null && sections.some((section) => section._id === requestedSectionId)
      ? requestedSectionId
      : null;
    const nextSectionId = requested ?? nextPolicySectionId({ sections, activeSectionId });
    if (nextSectionId !== activeSectionId) setActiveSectionId(nextSectionId);
  }, [sections, activeSectionId, requestedSectionId]);

  useEffect(() => {
    setSectionDraft((current) => nextPolicySectionDraftState(current, {
      type: "sectionLoaded",
      body: selectedSection?.body ?? "",
    }));
  }, [selectedSection?._id, selectedSection?.body]);

  useEffect(() => {
    const nextLegislationId = nextPolicyMappingLegislationId({
      legislation: subscribedLegislation,
      selectedLegislationId,
      preferredLegislationId,
    });
    if (nextLegislationId !== selectedLegislationId) setSelectedLegislationId(nextLegislationId);
  }, [preferredLegislationId, selectedLegislationId, subscribedLegislation]);

  useEffect(() => {
    setMappingWorkflow((current) => nextPolicyMappingWorkflowState(current, { type: "reset" }));
  }, [selectedLegislationId, obligationSetId, mappingResetKey]);

  useEffect(() => {
    if (alertsRequested) setStaffAssignmentOpen(true);
  }, [alertsRequested]);

  const updateDraftBody = (body: string) => {
    setSectionDraft((current) => nextPolicySectionDraftState(current, { type: "bodyInput", body }));
  };

  const saveSection = async () => {
    if (companyId === null || selectedSection === null || !canSave) return;
    setSectionDraft((current) => nextPolicySectionDraftState(current, { type: "saveStarted" }));
    try {
      await updatePolicySection({ companyId, sectionId: selectedSection._id, body: draftBody });
      setSectionDraft((current) => nextPolicySectionDraftState(current, { type: "saveSucceeded" }));
    } catch (e) {
      setSectionDraft((current) => nextPolicySectionDraftState(current, {
        type: "saveFailed",
        error: caughtErrorMessage(e, "Could not save document section"),
      }));
    }
  };

  const runPolicyMapping = async () => {
    if (companyId === null || id === undefined || !canMap) return;
    setMappingWorkflow((current) => nextPolicyMappingWorkflowState(current, { type: "started" }));
    try {
      const result = obligationSetId !== null
        ? await mapPolicyToObligationSet({
            companyId,
            policyId: id,
            obligationSetId,
          })
        : selectedLegislationId === null
          ? null
          : await mapPolicyToLegislation({
              companyId,
              policyId: id,
              legislationId: selectedLegislationId,
            });
      if (result === null) throw new Error("Select an obligation source before mapping");
      setMappingWorkflow((current) => nextPolicyMappingWorkflowState(current, { type: "succeeded", result }));
    } catch (e) {
      setMappingWorkflow((current) => nextPolicyMappingWorkflowState(current, {
        type: "failed",
        error: caughtErrorMessage(e, "Could not generate AI mapping"),
      }));
    }
  };

  if (companyId === null) {
    return <p className="px-9 py-7 text-slate-500">Preparing document workspace...</p>;
  }

  if (policy === undefined) {
    return <p className="px-9 py-7 text-slate-500">Loading document...</p>;
  }

  if (policy === null) {
    return (
      <div className="mx-auto max-w-[720px] px-6 py-12 text-center">
        <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-slate-100 text-slate-400"><Icon name="info" size={21} /></div>
        <h2 className="font-serif text-[23px] font-semibold text-slate-900">Document not found</h2>
        <p className="mt-2 text-sm text-slate-500">This document may have been removed or may belong to another company.</p>
        <div className="mt-5"><Link to="/documents"><Button variant="outline" icon="arrowRight">Back to Documents</Button></Link></div>
      </div>
    );
  }

  const portfolioMapUrl = policyPortfolioMapUrl(policy._id, selectedSection?._id ?? null);

  return (
    <div className="mx-auto w-full max-w-[1180px] px-6 py-7 lg:px-9">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-5 border-b border-slate-300 pb-5">
        <div className="min-w-0">
          <button onClick={() => navigate(portfolioMapUrl)} className="mb-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 hover:text-[var(--accent-dark)]">
            <Icon name="arrowRight" size={13} className="rotate-180" /> Document map
          </button>
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            DOCUMENT::EDITOR{selectedSection === null ? "" : ` / ${selectedSection.sectionKey}`}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusBadge status={policy.status} />
            <ScopeBadge label={businessScopeDisplayName(policy.scope)} />
            <span className="rounded bg-slate-50 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">Company document</span>
          </div>
          <h2 className="mt-3 font-serif text-[30px] font-semibold leading-tight tracking-tight text-slate-950">{policy.title}</h2>
          <p className="mt-2 max-w-[760px] text-[14px] leading-relaxed text-slate-500">
            Edit the company document text Lawstream uses when matching legislative changes to document action.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" icon="users" onClick={() => setStaffAssignmentOpen(true)}>Assign staff to monitor document</Button>
          <Button variant="outline" icon="layers" onClick={() => navigate(portfolioMapUrl)}>Return to map</Button>
          <Button icon="check" onClick={saveSection} disabled={!canSave}>{policySectionSaveButtonLabel(saveState)}</Button>
        </div>
      </div>

      {canWrite && <DocumentDetailsEditor key={`${policy._id}-${selectedSection?._id}`} policy={policy} section={selectedSection} onDirtyChange={setDetailsDirty} />}
      <section className="grid gap-5 lg:grid-cols-[17rem_1fr_20rem]">
        <Card className="p-4">
          <h3 className="text-[14px] font-semibold text-slate-900">Document sections</h3>
          <p className="mt-1 text-[12.5px] leading-snug text-slate-400">Choose the clause to edit.</p>
          {sections.length === 0 ? (
            <div className="mt-4 rounded-lg bg-slate-50 px-3 py-3 text-[13px] text-slate-500 ring-1 ring-slate-100">No sections have been extracted for this document yet.</div>
          ) : (
            <div className="mt-4 space-y-2">
              {sections.map((section) => (
                <button
                  key={section._id}
                  onClick={() => navigate(`/documents/${policy._id}?section=${section._id}`, { replace: true })}
                  className={cx(
                    "block w-full rounded-lg px-3 py-2 text-left text-[13px] ring-1 transition",
                    selectedSection?._id === section._id
                      ? "bg-[var(--accent-soft)] text-[var(--accent-dark)] ring-[color:var(--accent)]/20"
                      : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50",
                  )}
                >
                  <span className="block font-semibold">{section.heading}</span>
                  <span className="mt-0.5 block font-mono text-[11px] opacity-70">{section.sectionKey}</span>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-3.5">
            <h3 className="text-[14px] font-semibold text-slate-900">Company document wording</h3>
            <p className="mt-0.5 text-[12.5px] text-slate-400">This is the company-owned document, not the source legislation.</p>
          </div>
          <div className="px-5 py-5">
            {selectedSection === null ? (
              <div className="rounded-lg bg-slate-50 px-4 py-4 text-[13.5px] text-slate-500 ring-1 ring-slate-100">
                Load or extract document sections before editing this document.
              </div>
            ) : (
              <div>
                <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Selected clause</div>
                    <h3 className="mt-1 text-[17px] font-semibold text-slate-900">{selectedSection.heading}</h3>
                  </div>
                  <button className="text-[12.5px] font-medium text-slate-500 hover:text-slate-800" onClick={() => updateDraftBody(selectedSection.body)}>Reset to saved</button>
                </div>
                <textarea
                  aria-label={`Document clause ${selectedSection.heading}`}
                  readOnly={!canWrite}
                  value={draftBody}
                  onChange={(e) => updateDraftBody(e.target.value)}
                  className="min-h-[22rem] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[14px] leading-relaxed text-slate-800 shadow-sm focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]"
                />
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Button icon="check" onClick={saveSection} disabled={!canSave}>{policySectionSaveButtonLabel(saveState)}</Button>
                  <span className={cx("text-[12.5px]", policySectionDraftToneClass(draftView.tone))}>
                    {draftView.message}
                  </span>
                </div>
              </div>
            )}
          </div>
        </Card>

        <aside className="space-y-5">
          <Card className="p-5">
            <h3 className="text-[14px] font-semibold text-slate-900">Document details</h3>
            <div className="mt-4 space-y-3 text-[13px] text-slate-600">
              <InfoRow label="Area" value={businessScopeDisplayName(policy.scope)} />
              <InfoRow label="Owner" value={policy.ownerName} />
              <InfoRow label="Department" value={policy.department} />
              <InfoRow label="Source" value={policy.sourceName ?? "Company document"} />
              <InfoRow label="Updated" value={formatNullableAuDate(policy.updatedAt, "Not set")} />
              <InfoRow label="Next review" value={formatNullableAuDate(policy.nextReviewAt, "Not set")} />
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-[14px] font-semibold text-slate-900">Matching context</h3>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-500">Lawstream compares these clauses against monitored legislative updates and creates document review tasks when wording may need to change.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {policy.tags.map((tag) => (
                <span key={tag} className="rounded bg-slate-50 px-2 py-1 text-[12px] font-medium text-slate-600 ring-1 ring-slate-200">{tag}</span>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-[14px] font-semibold text-slate-900">AI obligation mapping</h3>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-500">
              Generate draft mappings from this document to selected law or official document obligations. AI mappings need human verification before acceptance.
            </p>
            {obligationSetId !== null ? (
              <div className="mt-4 grid gap-3">
                <div className="rounded-lg bg-[var(--accent-soft)] px-3 py-3 ring-1 ring-[color:var(--accent)]/20">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--accent-dark)]">
                    Saved obligation set
                  </p>
                  <p className="mt-1 text-[13.5px] font-semibold text-slate-800">
                    {obligationSet === undefined
                      ? "Loading..."
                      : obligationSet?.name ?? "Obligation set unavailable"}
                  </p>
                  {obligationSet !== null && obligationSet !== undefined && (
                    <p className="mt-1 text-[12px] text-slate-600">
                      {obligationSet.items.length} selected obligations
                    </p>
                  )}
                </div>
                <Button icon="layers" disabled={!canMap} onClick={() => void runPolicyMapping()}>
                  {mappingState === "running" ? "Mapping..." : "Map document to saved set"}
                </Button>
                {obligationSet === null && (
                  <p className="text-[12.5px] text-red-600">This saved obligation set is no longer available.</p>
                )}
                {draftChanged && <p className="text-[12.5px] text-amber-700">Save the current clause before mapping.</p>}
                {mappingState === "error" && <p className="text-[12.5px] leading-relaxed text-red-600">{mappingError ?? "Could not generate AI mapping."}</p>}
              </div>
            ) : subscribedLegislation.length === 0 ? (
              <div className="mt-4 rounded-lg bg-amber-50 px-3 py-3 text-[13px] leading-relaxed text-amber-800 ring-1 ring-amber-200">
                Add subscribed legislation or an official document before mapping.
              </div>
            ) : (
              <div className="mt-4 grid gap-3">
                <label htmlFor="policy-mapping-source" className="text-[12px] font-medium text-slate-600">
                  Obligation source
                </label>
                <select
                  id="policy-mapping-source"
                  value={selectedLegislationId ?? ""}
                  onChange={(e) => setSelectedLegislationId(policyMappingLegislationIdFromInput({ legislation: subscribedLegislation, value: e.target.value, fallback: selectedLegislationId }))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-700 shadow-sm focus:border-[color:var(--accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]"
                >
                  {subscribedLegislation.map((legislation) => (
                    <option key={legislation._id} value={legislation._id}>{legislation.title}</option>
                  ))}
                </select>
                <Button icon="layers" disabled={!canMap} onClick={() => void runPolicyMapping()}>
                  {mappingState === "running" ? "Mapping..." : "Generate mapping"}
                </Button>
                {draftChanged && <p className="text-[12.5px] text-amber-700">Save the current clause before mapping.</p>}
                {mappingState === "error" && <p className="text-[12.5px] leading-relaxed text-red-600">{mappingError ?? "Could not generate AI mapping."}</p>}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h3 className="text-[14px] font-semibold text-slate-900">Actions</h3>
            <div className="mt-4 grid gap-2">
              <Button variant="outline" icon="plus" onClick={() => navigate("/documents/import" + (obligationSetId === null ? "" : `?obligationSet=${encodeURIComponent(obligationSetId)}`))}>Import another document</Button>
              <Button variant="outline" icon="layers" onClick={() => navigate(portfolioMapUrl)}>Return to document map</Button>
              <Button variant="outline" icon="book" onClick={() => navigate("/browse")}>View legal register</Button>
            </div>
          </Card>
        </aside>
      </section>

      {mappingResult !== null && (
        <>
          <PolicyMappingResultPanel result={mappingResult} target={mappingTarget} />
          {obligationSetId !== null && (
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                icon="layers"
                onClick={() => navigate(portfolioMapUrl)}
              >
                Review generated mappings
              </Button>
            </div>
          )}
        </>
      )}
      <PolicyAlertRecipientsModal companyId={companyId} policyId={policy._id} open={staffAssignmentOpen} onClose={() => setStaffAssignmentOpen(false)} />
    </div>
  );
}

function PolicyMappingResultPanel({ result, target }: { result: AiPolicyMappingResult; target: PolicyMappingTarget | null }) {
  return (
    <Card className="mt-5 overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div>
          <h3 className="text-[15px] font-semibold text-slate-900">AI mapping result</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px] text-slate-500">
            {target?.jurisdiction !== null && target?.jurisdiction !== undefined && (
              <JurisBadge code={target.jurisdiction} />
            )}
            <span>{target?.title ?? "Selected obligation source"}</span>
            {target !== null && <span>· {target.subtitle}</span>}
          </div>
        </div>
        <span className="rounded bg-amber-50 px-2 py-1 text-[12px] font-semibold text-amber-700 ring-1 ring-amber-200">AI unverified</span>
      </div>
      <div className="space-y-5 px-5 py-5">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
          {result.caveat}
        </div>
        <p className="text-[14px] leading-relaxed text-slate-700">{result.summary}</p>

        <div>
          <h4 className="text-[13px] font-semibold uppercase tracking-wide text-slate-400">Mapped obligations</h4>
          {result.mappings.length === 0 ? (
            <p className="mt-2 text-[13.5px] leading-relaxed text-slate-500">
              No usable mapping rows were generated from the selected excerpts. Try another obligation source or add more document wording.
            </p>
          ) : (
            <div className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
              {result.mappings.map((row, index) => <PolicyMappingRow key={row.legislationSectionId + index} row={row} />)}
            </div>
          )}
        </div>

        {result.missedObligations.length > 0 && (
          <div>
            <h4 className="text-[13px] font-semibold uppercase tracking-wide text-slate-400">Likely missed obligations</h4>
            <div className="mt-3 divide-y divide-red-100 rounded-lg border border-red-200 bg-red-50/50">
              {result.missedObligations.map((row, index) => <PolicyMappingRow key={row.legislationSectionId + index} row={row} compact />)}
            </div>
          </div>
        )}

        {result.nextSteps.length > 0 && (
          <div>
            <h4 className="text-[13px] font-semibold uppercase tracking-wide text-slate-400">Suggested next steps</h4>
            <ul className="mt-2 space-y-1.5 text-[13px] leading-relaxed text-slate-600">
              {result.nextSteps.map((step, index) => <li key={index}>{step}</li>)}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

function PolicyMappingRow({ row, compact = false }: { row: AiPolicyMappingResult["mappings"][number]; compact?: boolean }) {
  return (
    <div className={cx("px-4", compact ? "py-3" : "py-4")}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <PolicyMappingCoverageBadge status={row.coverageStatus} />
        <PolicyMappingConfidenceBadge confidence={row.confidence} />
        <span className="text-[12.5px] font-medium text-slate-500">{row.obligationSection}</span>
      </div>
      <p className="text-[13.5px] leading-relaxed text-slate-700">{row.obligationText}</p>
      <p className="mt-2 text-[13px] leading-relaxed text-slate-500"><span className="font-medium text-slate-700">Document location:</span> {row.policyLocation}</p>
      <p className="mt-1 text-[13px] leading-relaxed text-slate-500"><span className="font-medium text-slate-700">Why:</span> {row.mappingRationale}</p>
      {row.gap !== "" && <p className="mt-1 text-[13px] leading-relaxed text-slate-500"><span className="font-medium text-slate-700">Gap:</span> {row.gap}</p>}
      <p className="mt-1 text-[13px] leading-relaxed text-slate-500"><span className="font-medium text-slate-700">Action:</span> {row.recommendedAction}</p>
    </div>
  );
}

function PolicyMappingCoverageBadge({ status }: { status: PolicyObligationCoverageStatus }) {
  return (
    <span className={cx("rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1", policyMappingCoverageToneClass(status))}>
      {policyMappingCoverageLabel(status)}
    </span>
  );
}

function PolicyMappingConfidenceBadge({ confidence }: { confidence: PolicyMappingConfidence }) {
  return (
    <span className={cx("rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1", policyMappingConfidenceToneClass(confidence))}>
      {policyMappingConfidenceLabel(confidence)}
    </span>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-slate-400">{label}</span>
      <span className="text-right font-medium text-slate-700">{value}</span>
    </div>
  );
}


function ScopeBadge({ label }: { label: string }) {
  return <span className={cx("rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1", businessScopeBadgeToneClass())}>{label}</span>;
}

function StatusBadge({ status }: { status: PolicyStatus }) {
  return (
    <span className={cx("rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1", policyStatusToneClass(status))}>
      {policyStatusLabel(status)}
    </span>
  );
}
